import { ProductDocsWidget } from '../../widgets/ProductDocsWidget';
import { SiteRoute } from '@journeyapps/package-types';
import * as React from 'react';
import styled from '@emotion/styled';
import { PageWidget } from '../../widgets/layout/PageWidget';
import { ContentWidget } from '../../widgets/ContentWidget';
import { CrumbWidget } from '../../widgets/CrumbWidget';
import { SectionWidget } from '../../widgets/layout/SectionWidget';

const image_schema = require('../../../media/stack/schema.png');
const image_orm = require('../../../media/stack/database-orm.png');
const image_audit_logs = require('../../../media/stack/audit-logs.png');
const image_webhooks = require('../../../media/stack/cloudcode-tasks.png');

export const AppBackendPage: React.FC = (props) => {
  return (
    <PageWidget width={1100} title="App Backend">
      <ContentWidget>
        <h1>
          <CrumbWidget parts={['Platform stack', 'App Backend']} />
        </h1>
        <p>
          The App Backend provides cloud data storage for applications, CloudCode tasks, and external systems. It stores
          records and uploaded files, synchronizes data with Runtime apps, and gives CloudCode tasks direct access
          through the ORM. External systems can query and update records through the Backend API.
        </p>
        <p>
          Each project has separate testing, staging, and production deployments, so you can develop and validate
          changes with separate data before releasing them to production. Backends are available in multiple regions,
          with shared tenancy or separately arranged dedicated tenancy for Enterprise customers.
        </p>
        <p>
          Dedicated backends run on JourneyApps infrastructure, with database and file storage provided by default. At
          your request, our team can also set up connections to a MongoDB Atlas database you run on AWS or Azure, file
          storage you run on Amazon S3 or Azure Blob Storage, or both. You provide the connection details; the backend
          continues to run on our infrastructure in a dedicated capacity. Contact us to arrange setup.
        </p>
        <SectionWidget
          image={image_schema}
          label={'Data schema and ORM access'}
          content="All data stored on the backends are backed by an advanced schema, with support for complex types such as email, URLs, GPS locations
          and more. Data can not only be accessed via external APIs, but are also available via an advanced ORM with full TypeScript support."
          index={1}
        ></SectionWidget>
        <SectionWidget
          image={image_orm}
          ltr={false}
          link={SiteRoute.CLOUD_CODE}
          label={'Access data directly in CloudCode'}
          content="While data is synced both online and offline to devices, it is also directly accessible in CloudCode using the same advanced
          ORM."
          index={2}
        ></SectionWidget>
        <SectionWidget
          image={image_audit_logs}
          label={'Elevated access and audit logs'}
          content="When data is changed or accessed in any way, the backend keeps an audit log of absolutely everything. For production and staging deployments,
          backend users also need to elevate their access in order to access the data. This ensures the highest level of data integrity and trust."
          index={3}
        ></SectionWidget>
        <p>Includes all the features you would expect from an advanced data server:</p>
        <ul>
          <li>A centralized changelog for all data.</li>
          <li>Opaque and JWT token access.</li>
          <li>Ability to expire data and data fields automatically.</li>
          <li>Data controls to assist with GDPR compliance.</li>
          <li>Scalable with fine tune-able controls for rate limiting.</li>
          <li>Support for multiple regions: US, EU, Australia (AU), Japan (JP), Brazil (BR)</li>
        </ul>
      </ContentWidget>
      <SectionWidget
        image={image_webhooks}
        label={'Webhooks on change events'}
        content="The backend ships with support for firing arbitrary webhooks when models are created, updated or deleted. These can be wired up to external integrations
        or even vertically connected to CloudCode tasks enabling advanced computational and reactive cloud computing that is entirely data-driven."
        index={1}
      ></SectionWidget>
      <ProductDocsWidget
        title="Build on the App Backend"
        description="Define your data model, control access, and connect external systems through the API."
        topics={['Data Model', 'Backend API']}
      />
    </PageWidget>
  );
};
namespace S {
  export const Container = styled.div``;
}
